import { ClientError } from '@ricoh/ria.client.core/errors';

import type { IntegrationProvider, IntegrationType } from '~/common/model/api/integration';

const INTEGRATION_PROVIDERS: Record<IntegrationType, IntegrationProvider> = {
  microsoft: 'Microsoft',
  outlook: 'Outlook',
  awsS3Bucket: 'AwsS3Bucket',
  sftp: 'Sftp',
  natifAI: 'NatifAI',
  azureAI: 'AzureAI',
  rcip: 'Rcip',
  docuware: 'Docuware',
};

/**
 * Prepare Integration Provider.
 *
 * @template TIntegrationType Integration type.
 * @param {IntegrationProvider} provider Transform provider.
 *
 * @returns {TIntegrationType} Integration Provider.
 */
export function prepareIntegrationProvider<TIntegrationType extends IntegrationType>(provider: IntegrationProvider): TIntegrationType {
  const type = Object.keys<TIntegrationType>(INTEGRATION_PROVIDERS).find((key) => INTEGRATION_PROVIDERS[key] === provider);
  if (!type) {
    throw new ClientError({
      module: 'service',
      message: 'Invalid integration provider',
      cause: provider,
    });
  }

  return type;
}

/**
 * Transform Integration provider.
 *
 * @template TValidProviders Valid providers.
 * @param {IntegrationType} type Integration type.
 * @param {IntegrationProvider[]} validProviders Valid providers.
 *
 * @returns {IntegrationProvider} Integration provider.
 */
export function transformIntegrationProvider<TValidProviders extends IntegrationProvider = IntegrationProvider>(
  type: IntegrationType,
  validProviders?: TValidProviders[],
): TValidProviders {
  const provider = INTEGRATION_PROVIDERS[type];
  if (!provider || (validProviders && !validProviders.includes(provider))) {
    throw new ClientError({
      module: 'service',
      message: 'Invalid integration type',
      cause: type,
    });
  }

  return provider as TValidProviders;
}
